import {
  LayoutDashboard,
  LibrarySquare,
  FileText,
  BrainCircuit,
  Bookmark,
  StickyNote,
  type LucideIcon,
} from "lucide-react"
import {
  ASSESSMENT_COMPLETE,
  QUESTION_GENERATION_COMPLETE,
  type LearnerNotification,
} from "../../../api/studentNotifications"

export interface StudentNavItemConfig {
  name: string
  shortName: string
  path: string
  icon: LucideIcon
}

export const STUDENT_BASE_PATH = "/learner"

export const studentNavItems: StudentNavItemConfig[] = [
  {
    name: "Dashboard",
    shortName: "Home",
    path: `${STUDENT_BASE_PATH}/dashboard`,
    icon: LayoutDashboard,
  },
  {
    name: "Question Library",
    shortName: "Questions",
    path: `${STUDENT_BASE_PATH}/questions`,
    icon: LibrarySquare,
  },
  {
    name: "Practice Papers",
    shortName: "Papers",
    path: `${STUDENT_BASE_PATH}/practice-papers`,
    icon: FileText,
  },
  {
    name: "AI Assessor",
    shortName: "AI",
    path: `${STUDENT_BASE_PATH}/ai-assessor`,
    icon: BrainCircuit,
  },
  {
    name: "Bookmarks",
    shortName: "Saved",
    path: `${STUDENT_BASE_PATH}/bookmarks`,
    icon: Bookmark,
  },
  {
    name: "Notes",
    shortName: "Notes",
    path: `${STUDENT_BASE_PATH}/notes`,
    icon: StickyNote,
  },
]

export const STUDENT_DASHBOARD_PATH = `${STUDENT_BASE_PATH}/dashboard`
export const STUDENT_QUESTIONS_PATH = `${STUDENT_BASE_PATH}/questions`
export const STUDENT_BOOKMARKS_PATH = `${STUDENT_BASE_PATH}/bookmarks`
export const STUDENT_NOTES_PATH = `${STUDENT_BASE_PATH}/notes`
export const STUDENT_PRACTICE_PAPERS_PATH = `${STUDENT_BASE_PATH}/practice-papers`

export const studentQuestionHref = (questionId: string) =>
  `${STUDENT_QUESTIONS_PATH}?id=${encodeURIComponent(questionId)}`

export const studentPracticePaperHref = (paperId: string, retake = false) =>
  `${STUDENT_PRACTICE_PAPERS_PATH}?paper=${encodeURIComponent(paperId)}${
    retake ? "&retake=1" : ""
  }`

export const studentPracticePaperAnalyticHref = (paperId: string) =>
  `${STUDENT_PRACTICE_PAPERS_PATH}/${encodeURIComponent(paperId)}/analytic`

export const STUDENT_AI_ASSESSOR_PATH = `${STUDENT_BASE_PATH}/ai-assessor`

export const studentPaperAssessorHref = (paperId: string) =>
  `${STUDENT_AI_ASSESSOR_PATH}/${encodeURIComponent(paperId)}`

export const studentAssessmentAttemptHref = (attemptId: string) =>
  `${STUDENT_AI_ASSESSOR_PATH}?attempt=${encodeURIComponent(attemptId)}`

const notificationId = (
  data: LearnerNotification["data"],
  key: string
): string | null => {
  const raw = data?.[key]
  return typeof raw === "string" && raw.trim() ? raw.trim() : null
}

/** Where a learner notification click should go. Null when it has no target. */
export const learnerNotificationHref = (
  notification: LearnerNotification
): string | null => {
  const paperId = notificationId(notification.data, "paper_id")
  const attemptId = notificationId(notification.data, "attempt_id")
  if (paperId && notification.notification_type === ASSESSMENT_COMPLETE) {
    return studentPaperAssessorHref(paperId)
  }
  if (paperId && notification.notification_type === QUESTION_GENERATION_COMPLETE) {
    return studentPracticePaperHref(paperId)
  }
  if (
    notification.notification_type === ASSESSMENT_COMPLETE &&
    attemptId
  ) {
    return studentAssessmentAttemptHref(attemptId)
  }
  return null
}

/** Opens the create-paper drawer on the practice papers page. */
export const STUDENT_PRACTICE_PAPERS_CREATE_PATH = `${STUDENT_PRACTICE_PAPERS_PATH}?create=1`
export const STUDENT_PROFILE_PATH = `${STUDENT_BASE_PATH}/profile`
